import type {ObservableValue} from '@github-ui/observable'
import {createContext, type PropsWithChildren, type RefObject, use, useCallback} from 'react'

import type {CopilotChatState} from './copilot-chat-reducer'

const ObservableChatStateContext = createContext<RefObject<ObservableValue<CopilotChatState> | null> | null>(null)

export function ObservableChatStateProvider({
  children,
  value,
}: PropsWithChildren<{value: RefObject<ObservableValue<CopilotChatState> | null> | null}>) {
  return <ObservableChatStateContext value={value}>{children}</ObservableChatStateContext>
}

/**
 * Returns a constant reference to a function that can be used inside a callback to get the current version of the chat state.
 *
 * The returned `getChatState()` function should only be used inside an event handler or callback. If it is used in a render
 * method, it may return outdated state.
 */
export function useGetChatState(): () => CopilotChatState {
  const o = use(ObservableChatStateContext)
  if (!o || !o.current) throw new Error('useGetChatState can only be used inside a CopilotChatProvider')
  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  return useCallback(() => o.current!.value, [o])
}

export function useObservableChatStateContext() {
  return use(ObservableChatStateContext)
}

try{ ObservableChatStateContext.displayName ||= 'ObservableChatStateContext' } catch {}
try{ ObservableChatStateProvider.displayName ||= 'ObservableChatStateProvider' } catch {}